/* eslint-disable react/no-unstable-nested-components */
import { createColumnHelper } from "@tanstack/react-table";
import CompanyCorporateActionsView from "components/company/v3/CompanyCorporateActionsView";
import classNames from "components/ui/classNames";
import getYear from "date-fns/getYear";
import type { IGlobalCorporateActionsQuery } from "lib/queries/__generated__/GlobalCorporateActionsQuery.generated";
import formatDateOrReturnHyphen from "lib/utils/formatDateOrReturnHyphen";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import parseDateYmd from "lib/utils/parseDateYmd";
import numeral from "numeral";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { IRightsIssuesCorporateAction } from "types/graphqlTypes";

interface AmericanRightsIssuesContentProps {
  data: IGlobalCorporateActionsQuery["rightsIssues"];
  isRenderPageDefault: boolean;
}
export default function AmericanRightsIssuesContent({ data, isRenderPageDefault }: AmericanRightsIssuesContentProps) {
  const [filterRightsIssuesByYear, setFilterRightsIssuesByYear] = useState<number | null>(null);
  const intl = useIntl();

  const columnHelper = createColumnHelper<IRightsIssuesCorporateAction>();

  const availableYears = useMemo(
    () => [
      ...new Set(
        data
          .map((rightsIssues) => {
            if (rightsIssues.excludingDate) {
              return getYear(parseDateYmd(rightsIssues.excludingDate));
            }

            return undefined;
          })
          .filter(notUndefinedOrNull),
      ),
    ],
    [data],
  );

  const arrayOfDataFilteredByYear = useMemo(
    () =>
      data?.filter((node) => {
        if (!filterRightsIssuesByYear) {
          return true;
        }

        return getYear(parseDateYmd(node.excludingDate)) === filterRightsIssuesByYear;
      }),
    [data, filterRightsIssuesByYear],
  );

  const columns = [
    columnHelper.accessor("recordDate", {
      header: () => (
        <div
          className={classNames(
            "w-24 bg-slate-50 px-2 py-1.5 text-sm font-semibold text-slate-700",
            "dark:bg-slate-800 dark:text-slate-300",
          )}
        >
          <FormattedMessage defaultMessage="Data-com" id="+YErWR" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "w-24 select-none",
            "whitespace-nowrap",
            "bg-slate-50 text-slate-600",
            "px-2 py-1.5 text-sm font-semibold text-slate-600",
            "group-hover:bg-violet-50 group-hover:text-violet-700",
          )}
        >
          <span>{formatDateOrReturnHyphen(intl, info.getValue())}</span>
        </div>
      ),
    }),
    columnHelper.accessor("payDate", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Pagamento" id="23+rmI" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span>{formatDateOrReturnHyphen(intl, info.getValue())}</span>
        </div>
      ),
    }),
    columnHelper.accessor("subscriptionPrice", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Subscrição (R$)" id="x/NIcW" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span>{info.getValue() || "-"}</span>
        </div>
      ),
    }),
    columnHelper.accessor("newShare", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Subscrição (%)" id="EbMyht" />
        </div>
      ),
      cell: (info) => {
        const value = info.getValue();

        return (
          <div
            className={classNames(
              "whitespace-nowrap",
              "px-4 py-1.5 text-sm font-normal",
              "text-slate-600 dark:text-slate-400",
              "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
            )}
          >
            <span>{value ? formatValue(value * 100) : "-"}%</span>
          </div>
        );
      },
    }),
  ];

  return (
    <CompanyCorporateActionsView<IRightsIssuesCorporateAction>
      data={arrayOfDataFilteredByYear || []}
      columns={columns}
      availableYears={availableYears}
      filterByYear={filterRightsIssuesByYear}
      setFilterByYear={setFilterRightsIssuesByYear}
      isRenderPageDefault={isRenderPageDefault}
    />
  );
}

function formatValue(value?: number | null) {
  if (!value) {
    return "-";
  }

  return numeral(value).format("0.00").replace(".00", "").replace(".", ",");
}
